// Copyright (c) StrangeBee 2023

import React, { ReactElement, useMemo } from 'react';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import useConfigList from '@Hooks/useConfigList';
import CustomFieldManage from '@Organisms/CustomField/CustomFieldManage/CustomFieldManage';
import { ObjectTypeV1 as ObjectType, CustomFieldOutput, AdminPermissionsEnum } from 'thehive-sdk';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import client from '@Client';
import CustomFieldListDetails from '@Organisms/CustomField/CustomFieldListDetails/CustomFieldListDetails';
import { DisplayTags } from '@Atoms/Display';
import ListProvider from '@Context/ListProvider';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { computeEventList } from '@Organisms/CustomField/eventList';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import usePermissions from '@Hooks/usePermissions';
import { useCurrentUser } from '@Queries/userQueries';
import { useListAllFields } from '@Queries/describeQueries';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { CustomFieldGroupEdit } from '@Organisms/CustomField/CustomFieldGroupEdit/CustomFieldGroupEdit';
import ActionList from '@Molecules/ActionList/ActionList';
import { ICONS } from '@Constants/iconsApp';
import { useDrawer } from '@Hooks/useDrawer';
import { computeListSelector } from '@Organisms/CustomField/selector';

import './CustomFieldList.scss';

const CustomFieldList = (): ReactElement => {
    const i = useLocalMessage();

    const config = useConfigList<CustomFieldOutput>({
        selector: computeListSelector(),
        controller: client.CustomFieldController,
        storageId: StorageFilterEnum.CUSTOM_FIELD,
        objectType: ObjectType.CUSTOM_FIELD,
    });

    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};
    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer({});

    const hasPermissions = usePermissions({
        permissionsNeeded: [AdminPermissionsEnum.MANAGE_CUSTOM_FIELD],
        permissionsList: permissions,
    });

    const {
        dataSource = [],
        columnsRenderer,
        listRefetch,
        status = 'loading',
        filterManager,
        paginationManager,
        table,
        storagePageManager,
        storageViewManager,
        rowSelectionCustomRenderer,
        stateSelectedKeys,
    } = config;

    const { selectedKeys, setSelectedKeys } = stateSelectedKeys;

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.CustomFieldController,
                locale: i,
                onSuccess: (msg) => {
                    listRefetch();
                    openMessage('success', msg);
                },
                onError: handleNotificationError,
            }),
        [i, listRefetch],
    );

    const columns = columnsRenderer([
        {
            title: i('customField.list.table.columns.group'),
            className: 'custom-field-list__column-group',
            render: ({ group = 'default' }): ReactElement => <div>{group}</div>,
        },
        {
            title: [{ label: i('customField.list.table.columns.details'), sortField: 'name' }],
            className: 'custom-field-list__column-details column_preview',
            render: (customField): ReactElement => <CustomFieldListDetails data={customField} />,
        },
        {
            title: i('customField.list.table.columns.mandatory'),
            className: 'custom-field-list__column-mandatory',
            render: ({ mandatory }) => (
                <div>
                    <i>{i(`general.${mandatory ? 'yes' : 'no'}`)}</i>
                </div>
            ),
        },
        {
            title: i('customField.list.table.columns.options'),
            className: 'custom-field-list__column-options',
            render: ({ options = [] }): ReactElement => (
                <div>
                    <DisplayTags tags={options.map((value) => value + '')} />

                    {options.length === 0 && <i>{i('customField.list.table.options.empty')}</i>}
                </div>
            ),
        },
        {
            title: i('customField.list.table.columns.type'),
            className: 'custom-field-list__column-type',
            render: ({ type = 'string' }): ReactElement => <div>{type}</div>,
        },
        {
            className: 'custom-field-list__column-actions',
            render: (customField): ReactElement | null =>
                hasPermissions ? (
                    <ActionsList
                        actionList={{
                            edit: {
                                label: i('general.edit'),
                                icon: <ICONS.EditIcon />,
                                CustomComponent: (triggerElem) => (
                                    <CustomFieldManage
                                        onFinish={eventList.onEdit}
                                        data={customField}
                                        triggerElem={triggerElem}
                                        customFieldList={dataSource}
                                    />
                                ),
                            },
                            delete: {
                                label: i('general.delete'),
                                confirm: true,
                                message: !customField?.extraData?.deletable
                                    ? i('customField.list.force')
                                    : i('customField.list.delete-confirm'),
                                query: () => eventList.onDelete(customField._id, !customField?.extraData?.deletable),
                                icon: <ICONS.DeleteIcon />,
                            },
                        }}
                        isIcon
                        scope="single"
                    />
                ) : null,
        },
    ]);

    const { data: describe } = useListAllFields();

    const rowSelection = rowSelectionCustomRenderer({
        label: i('common.selected'),
        render: (_, checkbox): ReactElement => <>{checkbox}</>,
    });

    return (
        <>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {hasPermissions && (
                            <AddButton
                                label={i('customField.list.header.add-custom-field')}
                                onClick={() => setVisibleCreate(true)}
                            />
                        )}

                        {storagePageManager && storageViewManager ? (
                            <ManageViewList
                                pageManager={storagePageManager}
                                viewManager={storageViewManager}
                                beforeCreate={(content: any): any => {
                                    filterManager.applyFilters();
                                    return { ...content, filters: filterManager.tempFilters };
                                }}
                            />
                        ) : null}

                        {hasPermissions ? (
                            <ActionList
                                actions={{
                                    bulkGroup: {
                                        label: 'Bulk edit group',
                                        icon: <ICONS.EditIcon />,
                                        CustomComponent: (triggerElem) => (
                                            <CustomFieldGroupEdit
                                                triggerElem={triggerElem}
                                                customFieldList={dataSource}
                                                onFinish={(group) =>
                                                    Promise.all(
                                                        selectedKeys.map((_id = '') =>
                                                            eventList.onEdit({ customFieldId: _id as string, group }),
                                                        ),
                                                    ).then(() => setSelectedKeys([]))
                                                }
                                            />
                                        ),
                                    },
                                }}
                                hidden={!selectedKeys.length}
                                scope="list"
                            />
                        ) : null}
                    </THList.Slot>
                    <THList.Slot name="NAVIGATION">
                        <ManageSwitch
                            label={i('common.filters')}
                            value={filterManager.editFilters}
                            onChange={(editFilters): void => {
                                filterManager.setEditFilters(editFilters);
                            }}
                            icon={<ICONS.FilterOutlined />}
                        />
                    </THList.Slot>
                    <THList.Slot name="CONTENT">
                        {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                            <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.CUSTOM_FIELD]}>
                                <Footer filterConfig={filterManager} />
                            </THFilter>
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    dataSource={dataSource}
                    className="custom-field-list__wrapper"
                    columns={columns}
                    rowKey="_id"
                    rowSelection={hasPermissions ? rowSelection : undefined}
                    emptyPlaceholder={
                        <DisplayEmptyData spin={status === 'loading'} label={i('customField.none')}>
                            {hasPermissions && (
                                <THButton type="link" onClick={() => setVisibleCreate(true)}>
                                    {i('customField.list.header.add-custom-field')}
                                </THButton>
                            )}
                        </DisplayEmptyData>
                    }
                />

                <THList.Footer>
                    <THList.Slot name="NAVIGATION">
                        <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                        <PaginationSize paginationConfig={paginationManager} />
                    </THList.Slot>
                </THList.Footer>
            </THList>

            {visibleCreate && (
                <CustomFieldManage
                    visible={visibleCreate}
                    onVisibleChange={setVisibleCreate}
                    onFinish={eventList.onCreate}
                    customFieldList={dataSource}
                />
            )}
        </>
    );
};

const path = RouteBuilder.compute('administration.customFields');

export const Route: RouteType = {
    path,
    menu: {
        label: 'mainLayout.adminMenu.customfields',
        icon: <ICONS.CustomFieldIcon />,
    },
    breadcrumb: {
        label: 'breadcrumb.global.custom-fields',
    },
    content: <CustomFieldList />,
    Context: ListProvider,
    space: ['admin'],
    permissionNeeded: [AdminPermissionsEnum.MANAGE_CUSTOM_FIELD],
    exact: true,
};
